/**
 * @fileoverview Cartão de produto responsivo com funcionalidades de favoritos e carrinho.
 * Exibe informações do produto com imagem, nome, marca e preço formatado.
 * Inclui modal de detalhes ao clicar no card.
 * 
 * @module ProductCard
 * @requires react-redux
 * @requires @mui/material
 * @since 1.0.0
 */

import { useMemo, memo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  IconButton,
  CardMedia,
  useMediaQuery,
  useTheme
} from "@mui/material";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import FavoriteOutlinedIcon from "@mui/icons-material/FavoriteOutlined";
import { addToCart } from "../../store/slices/cartSlice";
import { toggleFavorite } from "../../store/slices/favoritesSlice";
import { selectProductById } from "../../store/slices/globalDataSlice";
import { formatPrice } from "../../utils/priceUtils";
import ProductModal from "./ProductModal";

/**
 * Cartão de produto responsivo com funcionalidades de favoritos, carrinho e modal de detalhes.
 * 
 * Funcionalidades:
 * - Exibir imagem, nome, marca e preço do produto
 * - Botão de favorito (coração) no canto superior direito
 * - Botão de adicionar ao carrinho (adaptativo: ícone em mobile, texto em desktop)
 * - Clique no card abre modal com detalhes completos
 * - Hover effect com elevação do card
 * - Memoização para otimização de performance
 * 
 * @component
 * @param {Object} props - Propriedades do componente
 * @param {string|number} props.id - ID único do produto
 * @param {string} props.nome - Nome do produto
 * @param {string} props.marca - Marca do produto
 * @param {number} props.preco - Preço do produto em reais
 * @param {string} props.imagem - URL da imagem do produto
 * @param {string} props.categoria - Categoria do produto
 * @param {string} [props.descricao] - Descrição detalhada do produto
 * @returns {JSX.Element} Cartão responsivo com ações de favorito, carrinho e modal
 * 
 * @example
 * <ProductCard 
 *   id={1}
 *   nome="Produto Exemplo"
 *   marca="Marca X"
 *   preco={29.99}
 *   imagem="/path/to/image.jpg"
 *   categoria="Categoria A"
 *   descricao="Descrição do produto"
 * />
 */
function ProductCard({ id, nome, marca, preco, imagem, categoria, descricao }) {
  const dispatch = useDispatch();
  const favorites = useSelector((state) => state.favorites.items);
  const isFavorite = favorites.some(fav => fav.productId === id);
  const [modalOpen, setModalOpen] = useState(false);
  const cart = useSelector((state) => state.cart.items);
  // Selector memoizado por id para evitar re-renders desnecessários
  const selectProdutoById = useMemo(() => selectProductById(id), [id]);
  const produtoCompleto = useSelector(selectProdutoById);
  const semEstoque = Number(produtoCompleto?.quantidade_estoque ?? 0) <= 0;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  /**
   * Adiciona/remove produto dos favoritos.
   * @param {Event} e - Evento de clique
   */
  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    dispatch(toggleFavorite({ id, nome, marca, imagem, categoria }));
  };
  
  /**
   * Adiciona produto ao carrinho.
   * @param {Event} e - Evento de clique
   */
  const handleAddToCart = (e) => {
    e.stopPropagation();
    dispatch(addToCart({ id, nome, marca, preco, imagem, categoria }));
  };
  
  /**
   * Abre modal com detalhes do produto.
   */
  const handleCardClick = () => {
    setModalOpen(true);
  };
  
  /**
   * Fecha modal de detalhes.
   */
  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const formattedPrice = useMemo(() => {
    return formatPrice(preco);
  }, [preco]);

  return (
    <>
      <Card 
        onClick={handleCardClick}
        sx={{ 
          position: "relative", 
          width: { xs: '98%', sm: '100%' },
          minWidth: { xs: 0, sm: 280 },
          maxWidth: '100%',
          height: 350, 
          display: 'flex', 
          flexDirection: 'column',
          minHeight: 350,
          maxHeight: 350,
          flex: 1,
          cursor: 'pointer',
          '&:hover': {
            transform: 'translateY(-2px)',
            transition: 'transform 0.2s ease-in-out'
          }
        }}
      >
      {/* Alerta de indisponibilidade no canto superior esquerdo */}
      {semEstoque && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            zIndex: 2,
            bgcolor: 'warning.main',
            color: 'warning.contrastText',
            borderRadius: 1,
            px: 1,
            py: 0.25,
            fontSize: '0.65rem',
            fontWeight: 'bold',
            lineHeight: 1.4,
            boxShadow: 1,
          }}
          role="status"
          aria-label="Produto indisponível"
        >
          Indisponível
        </Box>
      )}
      <IconButton
        onClick={handleFavoriteClick}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 1,
          bgcolor: "rgba(255,255,255,0.8)",
          color: "#f44336"
        }}
        size="medium"
        aria-label={isFavorite ? "Remover dos favoritos" : "Adicionar aos favoritos"}
      >
        {isFavorite ? <FavoriteOutlinedIcon /> : <FavoriteBorderOutlinedIcon />}
      </IconButton>
      
      <Box sx={{ 
        height: 180, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: 'grey.50'
      }}>
        {imagem ? (
          <CardMedia
            component="img"
            sx={{ 
              width: 160,
              height: 160,
              objectFit: 'contain'
            }}
            image={imagem}
            alt={nome}
          />
        ) : (
          <Box
            component="img"
            src="/vika_icon_light.gif"
            alt="Produto sem imagem"
            sx={{
              width: 160,
              height: 160,
              objectFit: 'contain'
            }}
          />
        )}
      </Box>
      
      <CardContent sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column',
        height: 170,
        p: 2
      }}>
        <Typography variant="h6" component="h3" sx={{ 
          mb: 1, 
          fontSize: '1rem',
          height: { xs: 'auto', md: '2.5rem' },
          overflow: 'hidden',
          display: { xs: 'block', md: '-webkit-box' },
          WebkitLineClamp: { xs: 'none', md: 2 },
          WebkitBoxOrient: { xs: 'initial', md: 'vertical' },
          lineHeight: 1.25,
          textOverflow: { xs: 'ellipsis', md: 'initial' },
          whiteSpace: { xs: 'nowrap', md: 'initial' }
        }}>
          {nome}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ 
          mb: 2,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {marca}
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 'auto'
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" color="primary">
            {formattedPrice}
          </Typography>
          {isMobile ? (
            <IconButton 
              color="primary"
              onClick={handleAddToCart}
              disabled={semEstoque}
              sx={{ bgcolor: semEstoque ? 'action.disabledBackground' : 'primary.main', color: 'white', '&:hover': { bgcolor: semEstoque ? 'action.disabledBackground' : 'primary.dark' } }}
              aria-label="Adicionar ao carrinho"
            >
              <Box component="img" src="/imagens/icons/svg/vikanaweb-shopping-bag.svg" alt="Carrinho" sx={{ width: 24, height: 24, filter: semEstoque ? 'none' : 'invert(1)' }} />
            </IconButton>
          ) : (
            <Button 
              variant="contained" 
              size="small"
              onClick={handleAddToCart}
              disabled={semEstoque}
              startIcon={<Box component="img" src="/imagens/icons/svg/vikanaweb-shopping-bag.svg" alt="Carrinho" sx={{ width: 20, height: 20, filter: semEstoque ? 'none' : 'invert(1)' }} />}
            >
              Adicionar
            </Button>
          )}
        </Box>
      </CardContent>
      </Card>
      <ProductModal
        open={modalOpen}
        onClose={handleCloseModal}
        product={{
          id,
          nome,
          marca,
          preco,
          imagem,
          categoria,
          descricao
        }}
      />
    </>
  );
}

export default memo(ProductCard);